import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef } from 'react';

// Props interface based on the Vue component's props
interface ProductImageProps {
  /**
   * 是否展示颜色渐变图
   */
  showFrameBg?: boolean;
  /**
   * 镜腿图片
   */
  legBg?: string;
  /**
   * 镜框图
   */
  frameBg?: string;
  /**
   * 渐变颜色
   */
  frameBgColor?: string;
  /**
   * 镜片原图
   */
  originGlassImg?: string;
  /**
   * 是否渐变
   */
  isGradient?: boolean;
  /**
   * 是否为镜片
   */
  isGlass?: boolean;
  /**
   * 是否为灰色图片
   */
  isGrayImage?: boolean;
  /**
   * 盒子样式
   */
  boxClassName?: string;
}

// Define styled components for the various elements
const FrameBgWrap = styled('div')({
  position: 'relative',
  width: '100%',
  height: 'auto',
});

const FrameImage = styled('img')({
  position: 'absolute',
  left: 0,
  top: 0,
  display: 'block',
  width: '100%',
  height: 'auto',
  zIndex: 10,
});

const LegImage = styled('img')({
  position: 'absolute',
  left: 0,
  top: 0,
  display: 'block',
  width: '100%',
  height: 'auto',
  zIndex: 8,
});

const LensBgColor = styled('div')({
  aspectRatio: '2/1',
  position: 'relative',
  zIndex: 9,
  width: '100%',
  height: 'auto',
  transform: 'scale(0.9)',
});

const TransitionBg = styled(LensBgColor)({
  '@keyframes animation-bg': {
    '0%': {
      opacity: 0.1,
    },
    '100%': {
      opacity: 0.8,
    },
  },
  animationName: 'animation-bg',
  animationDuration: '2s',
  animationTimingFunction: 'ease-in-out',
  animationIterationCount: 'infinite',
  animationDirection: 'alternate',
});

const GlassImage = styled('img')({
  width: '100%',
  display: 'block',
});

export default function ProductImage({
  showFrameBg = false,
  legBg = '',
  frameBg = '',
  frameBgColor = '',
  originGlassImg = '',
  isGradient = true,
  isGlass = true,
  isGrayImage = false,
  boxClassName = '',
}: ProductImageProps) {
  const lensBgColorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // This corresponds to the Vue component's watch for $refs.lensBgColor
    if (lensBgColorRef.current && isGradient) {
      const element = lensBgColorRef.current;
      const handleAnimationStart = () => {
        // Since getAnimationDelay was not defined in the Vue component
        // We'll use a random delay value between 0s and 1s
        const randomDelay = `${Math.random()}s`;
        element.style.animationDelay = randomDelay;
      };

      element.addEventListener('animationstart', handleAnimationStart);

      return () => {
        element.removeEventListener('animationstart', handleAnimationStart);
      };
    }
  }, [isGradient]);

  return (
    <Box
      className={`${isGlass ? 'w-4/5' : 'w-full'} ${!isGrayImage || showFrameBg ? 'mix-blend-multiply' : ''} h-auto ${boxClassName}`}
    >
      {showFrameBg && (
        <FrameBgWrap sx={{ '--bg-color': frameBgColor }}>
          <LegImage src={legBg} className="leg_bg" alt="zeelool glasses" />
          {isGradient ? (
            <TransitionBg
              ref={lensBgColorRef}
              className="lens_bg_color transiton-bg"
              sx={{
                background: 'var(--bg-color)',
              }}
            />
          ) : (
            <LensBgColor
              ref={lensBgColorRef}
              className="lens_bg_color"
              sx={{
                background: 'var(--bg-color)',
              }}
            />
          )}
          <FrameImage src={frameBg} className="frame_bg" alt="zeelool glasses" />
        </FrameBgWrap>
      )}
      {!showFrameBg && originGlassImg && (
        <GlassImage className="glass-img" src={originGlassImg} alt="zeelool glasses" />
      )}
    </Box>
  );
}
